import {
  computed,
  getCurrentScope,
  onScopeDispose,
  reactive,
  type Ref,
  ref
} from "vue";

// Hand-rolled server-table core: a state container plus column defs, with
// the page cache, in-flight dedupe and next-page prefetch slice it needs.
// All row modeling stays on the server. Zero dependencies by design; if
// client-side row modeling is ever required, adopt a library INSIDE this
// module behind the same API rather than growing it here.
//
// The server speaks the DataTables server-side protocol (ajax-datatables-rails):
// draw / start / length / order[0][column|dir] / columns[i][data|search] /
// search[value], responding { draw, recordsTotal, recordsFiltered, data,
// aggregates }. All row modeling is server-side; this composable never sorts,
// filters, or pages rows itself.

export type CardRole = "primary" | "secondary" | "meta" | "status" | "actions";

export interface ServerColumn {
  data: string;
  title?: string;
  className?: string;
  footerClassName?: string;
  orderable?: boolean;
  searchable?: boolean;
  visible?: boolean;
  /** Redirect sort to another server column index (DataTables orderData). */
  orderData?: number;
  aggregateTitle?: string;
  filterName?: string;
  /** Card-mode placement for this column's value. */
  cardRole?: CardRole;
  createdCell?: (td: HTMLElement, cellData: unknown, row: TableRow) => void;
}

export type TableRow = Record<string, any>;

export interface SortSpec {
  id: string;
  desc: boolean;
}

export interface ServerTableOptions {
  /** DataTables JSON endpoint (query string stripped before use). */
  source: string;
  columns: () => ServerColumn[];
  pageSize?: number;
  initialSort?: SortSpec[];
  /** Extra request params merged in per fetch (URL filters, drill-through). */
  extraParams?: () => Record<string, string>;
  /** Called after each successful fetch has been applied to state. */
  onAfterFetch?: () => void;
  /** Warm the next page after each fetch (deferred to idle). Off by default:
   *  an unconditional prefetch doubles load on the report query tier. */
  prefetch?: boolean;
  debounceMs?: number;
  /** Cache entries older than this refetch on next use. */
  staleMs?: number;
}

interface CacheEntry {
  promise: Promise<any>;
  ts: number;
}

export interface ServerTable {
  // state
  rows: Ref<TableRow[]>;
  cardRows: Ref<TableRow[]>;
  recordsFiltered: Ref<number>;
  aggregates: Ref<Record<string, string>>;
  loading: Ref<boolean>;
  /** Message from the last failed fetch; null once a fetch succeeds. */
  error: Ref<string | null>;
  sorting: Ref<SortSpec[]>;
  colSearch: Record<string, string>;
  globalSearch: Ref<string>;
  pagination: { pageIndex: number; pageSize: number };
  // derived
  columns: Ref<ServerColumn[]>;
  visibleColumns: Ref<ServerColumn[]>;
  pageCount: Ref<number>;
  canLoadMore: Ref<boolean>;
  // pager (method names kept stable so swapping the engine stays mechanical)
  getCanPreviousPage: () => boolean;
  getCanNextPage: () => boolean;
  getPageCount: () => number;
  setPageIndex: (i: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  setPageSize: (n: number) => void;
  // sorting & search
  toggleSort: (data: string) => void;
  setSort: (spec: SortSpec[] | null) => void;
  setColumnSearch: (data: string, value: string) => void;
  clearColumnSearch: (data: string) => void;
  setGlobalSearch: (value: string) => void;
  // card accumulation
  loadMore: () => void;
  resetAccumulation: () => void;
  // lifecycle
  fetch: () => Promise<void>;
  refetch: () => Promise<void>;
  invalidate: () => void;
  buildRequest: (pageIndex?: number) => URLSearchParams;
}

const makeDebounce = (fn: () => void, ms: number) => {
  let t: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    clearTimeout(t);
    t = setTimeout(fn, ms);
  };
  run.cancel = () => clearTimeout(t);
  return run;
};

const MAX_CACHE_ENTRIES = 30;

export function useServerTable(options: ServerTableOptions): ServerTable {
  const {
    source,
    pageSize = 25,
    initialSort = [],
    extraParams = () => ({}),
    onAfterFetch,
    prefetch = false,
    debounceMs = 250,
    staleMs = 30_000
  } = options;

  // The query string on `source` is DISCARDED (the request is rebuilt from
  // state). A consumer whose initial_page_data bakes filters into the source
  // URL silently loses them — pass them through `extraParams` instead.
  if (import.meta.env.DEV && source.includes("?")) {
    console.warn(
      `useServerTable: the query string on source "${source}" is ignored; ` +
        "pass request params via extraParams"
    );
  }

  const columns = computed(() => options.columns() || []);
  const visibleColumns = computed(() =>
    columns.value.filter((c) => c.visible !== false)
  );

  const rows = ref<TableRow[]>([]);
  const cardRows = ref<TableRow[]>([]);
  const error = ref<string | null>(null);
  const recordsFiltered = ref(0);
  const aggregates = ref<Record<string, string>>({});
  const loading = ref(false);

  const sorting = ref<SortSpec[]>(initialSort);
  const colSearch = reactive<Record<string, string>>({});
  const globalSearch = ref("");
  const pagination = reactive({ pageIndex: 0, pageSize });

  let drawCounter = 0;
  let latestReq = 0;
  let disposed = false;

  const pageCount = computed(() =>
    Math.max(1, Math.ceil(recordsFiltered.value / pagination.pageSize))
  );
  const canLoadMore = computed(
    () => cardRows.value.length < recordsFiltered.value
  );

  // Server column index for a column key: order[0][column] / columns[i] are
  // positional indexes into the FULL column list (hidden columns included),
  // and a column may redirect its sort elsewhere via orderData (e.g. Status
  // sorts by a hidden alpha_status column).
  const serverIndex = (data: string) => {
    const col = columns.value.find((c) => c.data === data);
    if (col && typeof col.orderData === "number") return col.orderData;
    return columns.value.findIndex((c) => c.data === data);
  };

  const buildRequest = (pageIndex = pagination.pageIndex) => {
    const p = new URLSearchParams();
    p.set("draw", String(++drawCounter));
    p.set("start", String(pageIndex * pagination.pageSize));
    p.set("length", String(pagination.pageSize));
    p.set("search[value]", globalSearch.value || "");
    p.set("search[regex]", "false");

    columns.value.forEach((col, i) => {
      p.set(`columns[${i}][data]`, col.data || "");
      p.set(`columns[${i}][name]`, "");
      p.set(`columns[${i}][searchable]`, String(col.searchable !== false));
      p.set(`columns[${i}][orderable]`, String(col.orderable !== false));
      p.set(`columns[${i}][search][value]`, colSearch[col.data] || "");
      p.set(`columns[${i}][search][regex]`, "false");
    });

    const s = sorting.value[0];
    if (s) {
      p.set("order[0][column]", String(serverIndex(s.id)));
      p.set("order[0][dir]", s.desc ? "desc" : "asc");
    }

    Object.entries(extraParams()).forEach(([k, v]) => {
      if (v != null && v !== "") p.set(k, v);
    });
    return p;
  };

  // ---- Page cache: dedupe in-flight requests, serve warm pages, prefetch ----
  const cache = new Map<string, CacheEntry>();

  const cacheKey = (pageIndex: number) => {
    const p = buildRequest(pageIndex);
    p.delete("draw"); // draw is a nonce, never part of identity
    return p.toString();
  };

  const fetchPage = (pageIndex: number) => {
    const key = cacheKey(pageIndex);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < staleMs) return hit.promise;
    const base = source.split("?")[0];
    const promise = fetch(`${base}?${buildRequest(pageIndex).toString()}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then((res) => {
      if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
      return res.json();
    });
    cache.set(key, { promise, ts: Date.now() });
    if (cache.size > MAX_CACHE_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
    // Failures never stay cached — and only evict OUR entry: a late rejection
    // must not delete a fresh entry that replaced it under the same key.
    promise.catch(() => {
      if (cache.get(key)?.promise === promise) cache.delete(key);
    });
    return promise;
  };

  // Deferred so warming the next page never competes with the visible fetch,
  // and opt-in because it doubles load on the report endpoints.
  const prefetchNext = () => {
    if (!prefetch) return;
    const next = pagination.pageIndex + 1;
    if (next >= pageCount.value) return;
    const idle =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 200);
    idle(() => {
      if (!disposed) fetchPage(next);
    });
  };

  interface FetchOpts {
    /** Append this page to cardRows (loadMore) instead of replacing them. */
    append?: boolean;
    /** Page index to restore when an append fetch fails. */
    rollbackTo?: number;
  }

  const dedupeAppend = (existing: TableRow[], incoming: TableRow[]) => {
    // Offset pagination can hand back a row that shifted pages between
    // requests; a duplicate DT_RowId would double a card and its Vue key.
    const seen = new Set(
      existing.map((r) => r.DT_RowId).filter((id) => id != null)
    );
    return [
      ...existing,
      ...incoming.filter((r) => r.DT_RowId == null || !seen.has(r.DT_RowId))
    ];
  };

  const doFetch = async ({ append = false, rollbackTo }: FetchOpts = {}) => {
    const my = ++latestReq;
    loading.value = true;
    let succeeded = false;
    try {
      const json = await fetchPage(pagination.pageIndex);
      if (my !== latestReq || disposed) return; // superseded — keep previous rows
      rows.value = json.data ?? [];
      cardRows.value = append
        ? dedupeAppend(cardRows.value, rows.value)
        : [...rows.value];
      recordsFiltered.value = json.recordsFiltered ?? rows.value.length;
      aggregates.value = json.aggregates ?? {};
      error.value = null;
      succeeded = true;
    } catch (e) {
      if (my !== latestReq || disposed) return;
      error.value = e instanceof Error ? e.message : "Request failed";
      // An append that failed must leave the pager where it was, or a retry
      // skips a page and an unrelated later fetch lands on the wrong one.
      if (append && rollbackTo != null) pagination.pageIndex = rollbackTo;
    } finally {
      if (my === latestReq && !disposed) loading.value = false;
    }
    if (my !== latestReq || disposed || !succeeded) return;

    // Reconcile against a result set that shrank under the cursor: without
    // this the user is stranded on an empty page past the new last page.
    const maxPage = pageCount.value - 1;
    if (pagination.pageIndex > maxPage) {
      pagination.pageIndex = Math.max(0, maxPage);
      if (recordsFiltered.value > 0) {
        await doFetch();
        return;
      }
    }

    onAfterFetch?.();
    prefetchNext();
  };

  const debouncedFetch = makeDebounce(() => doFetch(), debounceMs);

  if (getCurrentScope()) {
    onScopeDispose(() => {
      disposed = true;
      debouncedFetch.cancel();
    });
  }

  // The reset rule: any change to what the rows MEAN (sort, any search, page
  // size) returns to page 0 and restarts card accumulation, so the pager and
  // the card count can never disagree.
  const resetToFirstPage = () => {
    cardRows.value = [];
    pagination.pageIndex = 0;
  };

  const setPageIndex = (i: number) => {
    pagination.pageIndex = Math.max(0, Math.min(i, pageCount.value - 1));
    doFetch();
  };
  const nextPage = () => setPageIndex(pagination.pageIndex + 1);
  const previousPage = () => setPageIndex(pagination.pageIndex - 1);
  const setPageSize = (n: number) => {
    pagination.pageSize = n;
    resetToFirstPage();
    doFetch();
  };

  const setSort = (spec: SortSpec[] | null) => {
    sorting.value = spec ?? [];
    resetToFirstPage();
    doFetch();
  };
  const toggleSort = (data: string) => {
    const s = sorting.value[0];
    setSort(
      s && s.id === data
        ? [{ id: data, desc: !s.desc }]
        : [{ id: data, desc: false }]
    );
  };

  const setColumnSearch = (data: string, value: string) => {
    colSearch[data] = value;
    resetToFirstPage();
    debouncedFetch();
  };
  const clearColumnSearch = (data: string) => {
    delete colSearch[data];
    resetToFirstPage();
    doFetch();
  };
  const setGlobalSearch = (value: string) => {
    globalSearch.value = value;
    resetToFirstPage();
    debouncedFetch();
  };

  const loadMore = () => {
    const prev = pagination.pageIndex;
    pagination.pageIndex = prev + 1;
    doFetch({ append: true, rollbackTo: prev });
  };
  const resetAccumulation = () => {
    resetToFirstPage();
    doFetch();
  };

  const invalidate = () => cache.clear();
  const refetch = () => {
    invalidate();
    // With cards accumulated past one page, refetching only the current page
    // would strand every earlier row (the list would collapse to e.g. rows
    // 51-75). Restart accumulation from page 0 instead of re-fetching pages
    // 0..N: after invalidation the dataset may have shifted arbitrarily, so
    // replaying N requests multiplies load and can still misalign rows.
    if (cardRows.value.length > pagination.pageSize) resetToFirstPage();
    return doFetch();
  };

  return {
    rows,
    cardRows,
    recordsFiltered,
    aggregates,
    loading,
    error,
    sorting,
    colSearch,
    globalSearch,
    pagination,
    columns,
    visibleColumns,
    pageCount,
    canLoadMore,
    getCanPreviousPage: () => pagination.pageIndex > 0,
    getCanNextPage: () => pagination.pageIndex < pageCount.value - 1,
    getPageCount: () => pageCount.value,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    toggleSort,
    setSort,
    setColumnSearch,
    clearColumnSearch,
    setGlobalSearch,
    loadMore,
    resetAccumulation,
    fetch: doFetch,
    refetch,
    invalidate,
    buildRequest
  };
}
